/* eslint-disable max-lines -- not split yet: a sibling component file needs its own story (check-story-coverage), so FilterItemWithExclude, the select list and FilterOptionLabel stay here */
/* eslint-disable noddi/filename-must-match-export -- exports DesktopFilterSidebar and MobileFilterDrawer, two wrappers around the internal FilterSidebar */
import { cn, NoddiIcon } from 'noddi-ui-common';
import { Fragment, useState, type ReactNode } from 'react';
import { Button } from '../../../../../base/button/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../../../../base/dropdown-menu';
import type { FilterControl } from '../../../../../types/filterControl';
import type { FilterSelectOption } from '../../../../../types/table';
import { NoddiCheckbox } from '../../../../Inputs/NoddiCheckbox';
import { NoddiInput } from '../../../../Inputs/NoddiInput';
import { NoddiBadge } from '../../../../Other/NoddiBadge';
import { NoddiSeparator } from '../../../../Other/NoddiSeparator';
import { TagsCircle } from '../../../../Other/TagsCircle';
import { NoddiDatePicker } from '../../../../Overlay/Date/NoddiDatePicker';
import { NoddiSheet } from '../../../../Overlay/NoddiSheet';
import { NoddiTooltip } from '../../../../Overlay/NoddiTooltip';
import { getActiveControlsCount, getControlActiveCount } from '../../helper';
import { resolveTableFilterTranslations, TableFilterTranslations } from '../../tableTranslationTypes';
import { useRequestFilterOptions } from '../../useFilterOptionsEnabled';
import { AppliedFilters } from './AppliedFilters';
import { DateRangeFilter } from './DateRangeFilter';
import { FilterLoadError } from './FilterLoadError';
import { FilterSkeleton } from './FilterSkeleton';
import {
  changeSelection,
  describedOptionName,
  getMatchingControls,
  getOptionsDisplay,
  type SelectFilterControl,
  type SelectionAction
} from './helpers';
import { NumberInputFilter } from './NumberInputFilter';
import { NumberRangeFilter } from './NumberRangeFilter';
import { TextInputFilter } from './TextInputFilter';
import { TimeRangeFilter } from './TimeRangeFilter';
import { useOptionFilter } from './useOptionFilter';

// Desktop filter sidebar
export function DesktopFilterSidebar({
  open,
  controls,
  className,
  resetPagination,
  translations
}: {
  open: boolean;
  controls: FilterControl[];
  className?: string;
  resetPagination?: () => void;
  translations?: Partial<TableFilterTranslations>;
}) {
  const texts = resolveTableFilterTranslations(translations);

  if (!open) {
    return null;
  }

  return (
    <div className={cn('flex max-w-[250px] min-w-[150px] flex-col bg-background', className)}>
      <FilterSidebar controls={controls} resetPagination={resetPagination} translations={texts} />
    </div>
  );
}

// Mobile filter drawer
export function MobileFilterDrawer({
  open,
  onOpenChange,
  controls,
  resetPagination,
  translations
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controls: FilterControl[];
  resetPagination?: () => void;
  translations?: Partial<TableFilterTranslations>;
}) {
  const texts = resolveTableFilterTranslations(translations);

  return (
    <NoddiSheet
      deferContentUntilOpen
      closeText={texts.done}
      open={open}
      onOpenChange={onOpenChange}
      title={texts.filters}
      description={texts.filterDescription}
    >
      <FilterSidebar isMobile controls={controls} resetPagination={resetPagination} translations={texts} />
    </NoddiSheet>
  );
}

function FilterSidebar({
  controls,
  resetPagination,
  isMobile,
  translations
}: {
  controls: FilterControl[];
  resetPagination?: () => void;
  isMobile?: boolean;
  translations?: TableFilterTranslations;
}) {
  const texts = translations ?? resolveTableFilterTranslations();
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [filterSearch, setFilterSearch] = useState('');
  const [expandedFilters, setExpandedFilters] = useState<Record<string, boolean>>({});
  const [showApplied, setShowApplied] = useState(false);
  // Mounted only while the sidebar or drawer is open, so option lists load on first open.
  useRequestFilterOptions();

  const { getSearchValue, handleSearchChange, getFilteredOptions } = useOptionFilter(searchTerms, setSearchTerms);
  const { visibleControls, shouldShowSearch } = getMatchingControls(controls, filterSearch);

  // Reset all = the list as a new visitor opens it: defaults come back. Clearing one filter lists everything.
  const resetAll = () => {
    controls.forEach((control) => control.reset());
    setSearchTerms({});
    setFilterSearch('');
    resetPagination?.();
  };

  const toggleFilterExpansion = (filterId: string) => {
    setExpandedFilters((prev) => ({
      ...prev,
      [filterId]: !prev[filterId]
    }));
  };

  const renderFilterTitle = (control: FilterControl) => {
    const activeCount = getControlActiveCount(control);

    const { title, description } = control;
    return (
      <div className='mb-2 flex items-center gap-2'>
        <p className='text-sm font-medium'>{title}</p>
        {description && <NoddiTooltip content={description} tooltipContentWrapperClassName='max-w-sm' />}
        <div className='flex items-center justify-center'>
          {activeCount > 0 && (
            <NoddiBadge variant='outline' className='size-[18px] border-none bg-gray-200/60 px-2 py-px text-[10px]'>
              {activeCount}
            </NoddiBadge>
          )}
        </div>
      </div>
    );
  };

  const renderControl = (control: FilterControl, index: number) => {
    switch (control.type) {
      case 'date':
        return (
          <div>
            {renderFilterTitle(control)}
            <NoddiDatePicker
              date={control.value}
              onDateChange={control.onChange}
              disabled={control.disabled}
              showClearButton
              className='min-w-min'
            />
          </div>
        );

      case 'date-range':
        return <DateRangeFilter control={control} renderFilterTitle={renderFilterTitle} />;

      case 'multi-select':
      case 'single-select':
        return (
          <SelectFilter
            control={control}
            title={renderFilterTitle(control)}
            index={index}
            totalFilters={visibleControls.length}
            options={getFilteredOptions(control)}
            searchValue={getSearchValue(control)}
            onSearchChange={(value) => handleSearchChange(control, value)}
            isExpanded={expandedFilters[control.id] ?? false}
            onToggleExpansion={() => toggleFilterExpansion(control.id)}
            texts={texts}
          />
        );

      case 'boolean':
        return (
          <div>
            {renderFilterTitle(control)}
            <div className='flex flex-col gap-2'>
              <NoddiCheckbox
                id={`${control.id}-yes`}
                checked={control.value === true}
                label={control.trueCount !== undefined ? `${texts.yes} (${control.trueCount})` : texts.yes}
                onCheckedChange={(checked) => {
                  control.onChange(checked ? true : null);
                }}
              />
              <NoddiCheckbox
                id={`${control.id}-no`}
                checked={control.value === false}
                label={control.falseCount !== undefined ? `${texts.no} (${control.falseCount})` : texts.no}
                onCheckedChange={(checked) => {
                  control.onChange(checked ? false : null);
                }}
              />
            </div>
          </div>
        );

      // One flag, so the title is the checkbox's own label rather than a heading above a Yes / No pair.
      case 'checkbox':
        return (
          <NoddiCheckbox
            id={control.id}
            checked={control.value}
            label={control.count !== undefined ? `${control.title} (${control.count})` : control.title}
            tooltip={typeof control.description === 'string' ? control.description : undefined}
            onCheckedChange={(checked) => control.onChange(checked === true)}
          />
        );

      case 'number-range':
        return <NumberRangeFilter control={control} renderFilterTitle={renderFilterTitle} />;

      case 'number-input':
        return <NumberInputFilter control={control} renderFilterTitle={renderFilterTitle} />;

      case 'text':
        return <TextInputFilter control={control} renderFilterTitle={renderFilterTitle} />;

      case 'time-range':
        return <TimeRangeFilter control={control} renderFilterTitle={renderFilterTitle} />;

      default:
        return null;
    }
  };

  const activeFiltersCount = getActiveControlsCount(controls);
  const canResetAll = controls.some((control) => !control.isInitial);

  const appliedToggle = showApplied ? (
    <Button type='button' variant='ghost' size='sm' onClick={() => setShowApplied(false)}>
      <NoddiIcon name='ArrowLeft' size='small' />
      {texts.allFilters}
    </Button>
  ) : (
    <Button
      type='button'
      variant='ghost'
      size='sm'
      disabled={activeFiltersCount === 0}
      onClick={() => setShowApplied(true)}
    >
      <NoddiIcon name='ListCheck' size='small' />
      {texts.viewApplied}
      {activeFiltersCount > 0 && (
        <NoddiBadge variant='outline' className='size-5 border-none bg-gray-200/60 px-2 py-px text-[10px]'>
          {activeFiltersCount}
        </NoddiBadge>
      )}
    </Button>
  );

  return (
    <div className='flex max-h-full flex-col'>
      {/* Always mounted, fixed height: the reset button appearing with the first active filter must not
          push the filter list down, or the option the user is about to click moves out from under the cursor. */}
      <div className='mb-5'>
        <div className='mb-1 flex h-8 shrink-0 items-center justify-between gap-4'>
          {/* The mobile drawer's sheet already has a "Filters" title, so its free header slot takes the toggle. */}
          {isMobile && appliedToggle}
          {!isMobile && (
            <div className='flex items-center gap-2'>
              <h3 className='px-1 text-lg font-semibold'>{texts.filters}</h3>
              {activeFiltersCount > 0 && (
                <span>
                  <NoddiBadge variant='outline' className='size-5 border-none bg-gray-200 px-2 py-px text-[10px]'>
                    {activeFiltersCount}
                  </NoddiBadge>
                </span>
              )}
            </div>
          )}
          <div className={cn('flex items-center gap-2', !canResetAll && 'invisible')}>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={resetAll}
              tabIndex={canResetAll ? undefined : -1}
              aria-hidden={!canResetAll}
            >
              {texts.resetAll}
              {isMobile && activeFiltersCount > 0 && (
                <NoddiBadge variant='outline' className='size-5 border-none bg-gray-200/60 px-2 py-px text-[10px]'>
                  {activeFiltersCount}
                </NoddiBadge>
              )}
            </Button>
          </div>
        </div>
        {/* Always mounted for the same reason as Reset all: it must not push the list down on the first selection. */}
        {!isMobile && <div className='mb-1 flex h-8 shrink-0 items-center'>{appliedToggle}</div>}
        <NoddiSeparator />
      </div>

      {showApplied && (
        <div className='minimal-scrollbar-hover flex flex-1 flex-col overflow-x-hidden overflow-y-auto px-1'>
          <AppliedFilters
            controls={controls.map((control) => withPageReset(control, resetPagination))}
            translations={texts}
          />
        </div>
      )}

      {!showApplied && shouldShowSearch && (
        <div className='mb-4 px-1'>
          <FilterSearchInput
            className='w-full'
            searchValue={filterSearch}
            onSearchChange={setFilterSearch}
            searchPlaceholder={texts.searchFilters}
            aria-label={texts.searchFilters}
            startAdornment={<NoddiIcon name='Search' size='small' className='text-muted-foreground' />}
          />
        </div>
      )}

      <div
        className={cn(
          'minimal-scrollbar-hover flex flex-1 flex-col gap-4 overflow-x-hidden overflow-y-auto px-1',
          showApplied && 'hidden'
        )}
      >
        {visibleControls.map((control, index) => (
          <Fragment key={control.id}>
            <div role='group' aria-label={control.title}>
              {renderControl(withPageReset(control, resetPagination), index)}
            </div>
            {index < visibleControls.length - 1 && <NoddiSeparator />}
          </Fragment>
        ))}
        {visibleControls.length === 0 && <EmptyState message={texts.noFiltersFound} />}
      </div>
    </div>
  );
}

/** A multi- or single-select control: search, load state, its options and the show more / less toggle. */
function SelectFilter({
  control,
  title,
  index,
  totalFilters,
  options,
  searchValue,
  onSearchChange,
  isExpanded,
  onToggleExpansion,
  texts
}: {
  control: SelectFilterControl;
  title: ReactNode;
  index: number;
  totalFilters: number;
  /** The options left after search. */
  options: FilterSelectOption[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  texts: TableFilterTranslations;
}) {
  const { displayOptions, hasManyOptions, shouldShowSearch, remainingCount } = getOptionsDisplay({
    control,
    options,
    searchValue,
    isExpanded
  });
  const hasAnyImage = displayOptions.some((option) => option.imageUrl);
  const isMulti = control.type === 'multi-select';
  const { load } = control;
  // `isLoading` covers options waiting for the sidebar to open; `isFetching` a retry after a failure.
  const isLoading = !!load && (load.isLoading || load.isFetching);
  const isError = load?.isError ?? false;

  return (
    <div>
      {title}

      {shouldShowSearch && (
        <div className={isMulti ? 'mb-3' : 'mb-2'}>
          <FilterSearchInput
            searchValue={searchValue}
            onSearchChange={onSearchChange}
            className={isMulti ? 'min-w-50' : 'w-full'}
            searchPlaceholder={texts.search(control.title)}
          />
        </div>
      )}
      {load?.isError && (
        <FilterLoadError
          title={control.title}
          isFetching={load.isFetching}
          onRetry={load.onRetry}
          translations={texts}
        />
      )}
      {isLoading && displayOptions.length === 0 && (
        <FilterSkeleton control={control} index={index} totalFilters={totalFilters} />
      )}
      {displayOptions.length > 0 && (
        <div className='flex flex-col gap-2'>
          {displayOptions.map((option) => {
            const isSelected =
              control.type === 'multi-select'
                ? control.value.include.includes(option.value)
                : control.value.include === option.value;
            const isExcluded = control.value.exclude.includes(option.value);
            const id = `${control.id}-${option.value}`;

            const optionLabel = <FilterOptionLabel option={option} isExcluded={isExcluded} hasAnyImage={hasAnyImage} />;

            if (control.canExclude) {
              return (
                <FilterItemWithExclude
                  key={id}
                  isSelected={isSelected}
                  isExcluded={isExcluded}
                  label={describedOptionName(option)}
                  optionLabel={optionLabel}
                  onSelect={(action) => changeSelection(control, option.value, action)}
                />
              );
            }

            return (
              <div key={id} className='flex items-center gap-2'>
                <NoddiCheckbox
                  id={id}
                  checked={isSelected}
                  label={optionLabel}
                  aria-label={describedOptionName(option)}
                  onCheckedChange={(checked) => changeSelection(control, option.value, checked ? 'include' : 'clear')}
                />
              </div>
            );
          })}
          <ExpansionControls
            hasManyOptions={hasManyOptions}
            isExpanded={isExpanded}
            remainingCount={remainingCount}
            onToggleExpansion={onToggleExpansion}
            showMoreLabel={texts.showMore(remainingCount)}
            showLessLabel={texts.showLess}
          />
        </div>
      )}
      {displayOptions.length === 0 && !isLoading && !isError && <EmptyState message={texts.noResultsFound} />}
    </div>
  );
}

// Reusable filter item with exclude dropdown menu
function FilterItemWithExclude({
  isSelected,
  isExcluded,
  label,
  optionLabel,
  onSelect
}: {
  isSelected: boolean;
  isExcluded: boolean;
  /** The checkbox's name, when its content would not give one (see `describedOptionName`). */
  label: string | undefined;
  optionLabel: ReactNode;
  /** Called once per click: include and exclude change together. */
  onSelect: (action: SelectionAction) => void;
}) {
  const handleClick = () => onSelect(isSelected && !isExcluded ? 'clear' : 'include');

  return (
    <div
      className='group flex cursor-pointer items-center gap-2'
      role='checkbox'
      aria-checked={isSelected ? true : isExcluded ? 'mixed' : false}
      aria-label={label}
    >
      {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- filter row that carries the click while the `role='checkbox'` wrapper around it carries the ARIA state; that split is a real bug, tracked as a follow-up */}
      <div className='flex flex-1 items-center gap-2' onClick={handleClick}>
        <div
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-[4px] border shadow-xs transition-shadow',
            isSelected && 'border-primary bg-primary text-primary-foreground',
            isExcluded && 'border-destructive bg-destructive text-destructive-foreground',
            !isSelected && !isExcluded && 'border-input'
          )}
        >
          {isSelected && <NoddiIcon name='Check' size='xSmall' />}
          {isExcluded && <NoddiIcon name='Minus' size='xSmall' />}
        </div>
        {optionLabel}
        {isExcluded && (
          <NoddiBadge variant='destructive' className='px-1 py-0 text-[10px]'>
            Excluded
          </NoddiBadge>
        )}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            // Reveal-on-hover, plus an always-visible fallback for touch. Tailwind 4 gates
            // `hover:` behind `@media (hover: hover)`, so `opacity-0 group-hover:opacity-100`
            // alone leaves this — the only trigger for Include/Exclude — permanently
            // invisible on a touch device, at any screen width. The hover-none override is
            // a single class matching `opacity-0`'s specificity and sorts after it, so it
            // wins on touch while leaving pointer devices exactly as they were.
            className='size-6 shrink-0 p-0 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:opacity-100'
            onClick={(e) => e.stopPropagation()}
          >
            <NoddiIcon name='EllipsisVertical' size='small' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onClick={() => onSelect('include')}>
            <NoddiIcon name='Check' size='small' className='text-primary' />
            Include
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSelect('exclude')}>
            <NoddiIcon name='Minus' size='small' className='text-destructive' />
            Exclude
          </DropdownMenuItem>
          {(isSelected || isExcluded) && (
            <DropdownMenuItem onClick={() => onSelect('clear')}>
              <NoddiIcon name='X' size='small' />
              Clear
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function ExpandButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <Button
      variant='ghost'
      size='sm'
      onClick={onClick}
      className='w-full justify-start pl-0 text-sm text-muted-foreground hover:text-foreground'
    >
      {label}
    </Button>
  );
}

function FilterSearchInput({
  searchValue,
  onSearchChange,
  className = '',
  searchPlaceholder,
  startAdornment,
  'aria-label': ariaLabel
}: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  className?: string;
  searchPlaceholder?: string;
  startAdornment?: ReactNode;
  /** Names the input when the placeholder alone would not say which list it narrows. */
  'aria-label'?: string;
}) {
  return (
    <NoddiInput
      className={className}
      placeholder={searchPlaceholder}
      aria-label={ariaLabel}
      startAdornment={startAdornment}
      value={searchValue}
      onChange={(e) => onSearchChange(e.target.value)}
    />
  );
}

function ExpansionControls({
  hasManyOptions,
  isExpanded,
  remainingCount,
  onToggleExpansion,
  showMoreLabel,
  showLessLabel
}: {
  hasManyOptions: boolean;
  isExpanded: boolean;
  remainingCount: number;
  onToggleExpansion: () => void;
  showMoreLabel: string;
  showLessLabel: string;
}) {
  if (!hasManyOptions) {
    return null;
  }

  return (
    <>
      {!isExpanded && remainingCount > 0 && <ExpandButton onClick={onToggleExpansion} label={showMoreLabel} />}
      {isExpanded && <ExpandButton onClick={onToggleExpansion} label={showLessLabel} />}
    </>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p className='py-2 text-sm text-muted-foreground'>{message}</p>;
}

function FilterOptionLabel({
  option,
  isExcluded,
  hasAnyImage
}: {
  option: FilterSelectOption;
  isExcluded: boolean;
  hasAnyImage: boolean;
}) {
  const labelText = option.description ? (
    <NoddiTooltip
      content={option.description}
      label={`More information about ${option.label}`}
      stopEventPropagation={false}
      tooltipContentWrapperClassName='max-w-xs'
      trigger={<span className='border-b border-dotted border-muted-foreground/50'>{option.label}</span>}
    />
  ) : (
    option.label
  );

  return (
    <span
      className={cn(
        'flex min-w-0 items-center gap-2 text-sm md:text-base',
        isExcluded && 'text-muted-foreground line-through'
      )}
    >
      {option.imageUrl ? (
        <img src={option.imageUrl} alt={option.label} className='size-6 shrink-0 object-contain' aria-hidden='true' />
      ) : (
        hasAnyImage && <div className='size-6 shrink-0' />
      )}
      {option.illustration ? (
        <span className='shrink-0' aria-hidden='true'>
          {option.illustration}
        </span>
      ) : null}
      {option.icon ? <NoddiIcon name={option.icon} size='small' className='shrink-0' aria-hidden='true' /> : null}
      {option.initials ? (
        <span
          aria-hidden='true'
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full text-[9px] leading-none font-semibold text-white uppercase',
            !option.colorHex && 'bg-muted-foreground'
          )}
          style={option.colorHex ? { backgroundColor: option.colorHex } : undefined}
        >
          {option.initials}
        </span>
      ) : option.colorHex ? (
        <TagsCircle colorHex={option.colorHex} className='shrink-0' />
      ) : null}
      <span className='flex min-w-0 flex-col'>
        <span className='truncate'>{labelText}</span>
        {option.secondaryLabels?.map((line) => (
          <span key={line} className='truncate text-xs text-muted-foreground'>
            {line}
          </span>
        ))}
      </span>
      {option.count !== undefined && <span className='text-xs text-muted-foreground'>({option.count})</span>}
    </span>
  );
}

/**
 * A filter change lists from the first page, as search, the quick filter and Reset all do: on page 4 of the old
 * result the new one may have fewer pages, and the list would show none of its rows.
 */
const withPageReset = <TControl extends FilterControl>(control: TControl, resetPagination?: () => void): TControl => {
  if (!resetPagination) {
    return control;
  }
  // Each control type takes its own value; the wrapper only passes it through.
  const onChange = control.onChange as (value: unknown) => void;
  return {
    ...control,
    onChange: (value: unknown) => {
      onChange(value);
      resetPagination();
    }
  };
};
